// stores/agencyStore.ts
import { create } from 'zustand';
import apiClient from '../apiSetup/axiosInterceptor';

export interface Agency {
  id: string;
  admin_id?: string;
  agency_code: string;
  name: string;
  description: string | null;
  state: string;
  city: string;
  pincode: string;
  created_at: string | null;
  updated_at: string | null;
}

interface SetupAgencyPayload {
  name: string;
  description?: string;
  state: string;
  city: string;
  pincode: string;
}

interface UpdateAgencyPayload {
  name?: string;
  description?: string;
  state?: string;
  city?: string;
  pincode?: string;
}

interface AgencyState {
  agency: Agency | null;
  loading: boolean;
  error: string | null;
  issues: string[];
  setupAgency: (payload: SetupAgencyPayload) => Promise<{ success: boolean; agency_code?: string }>;
  fetchAgency: () => Promise<void>;
  updateAgency: (payload: UpdateAgencyPayload) => Promise<boolean>;
  clearError: () => void;
}

export const agencyStore = create<AgencyState>((set) => ({
  agency: null,
  loading: false,
  error: null,
  issues: [],

  clearError: () => set({ error: null, issues: [] }),

  setupAgency: async (payload) => {
    set({ loading: true, error: null, issues: [] });
    try {
      const res = await apiClient.post<{
        success: boolean;
        message: string;
        id: string;
        agency_code: string;
      }>('/v1/agency', payload);

      set({ loading: false });
      return { success: true, agency_code: res.data.agency_code };
    } catch (err: any) {
      const message =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Failed to set up agency';
      const issues: string[] = err.response?.data?.issues || [];
      set({ error: message, issues, loading: false });
      return { success: false };
    }
  },

  fetchAgency: async () => {
    set({ loading: true, error: null });
    try {
      const res = await apiClient.get<{ success: boolean; data: Agency }>('/v1/agency');
      set({ agency: res.data.data, loading: false });
    } catch (err: any) {
      const message =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Failed to load agency';
      set({ error: message, loading: false, agency: null });
    }
  },

  updateAgency: async (payload) => {
    set({ loading: true, error: null });
    try {
      const res = await apiClient.patch<{ success: boolean; data: Agency }>('/v1/agency', payload);
      set({ agency: res.data.data, loading: false });
      return true;
    } catch (err: any) {
      const message =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Failed to update agency';
      set({ error: message, loading: false });
      return false;
    }
  },
}));